/**
 * Facturas — lista (`TDS-CORE` Parte 3 §8, LISTA).
 *
 * Hoy hay borradores (y los descartados, que se conservan, §1.1). La emisión
 * VERI*FACTU llega en la siguiente entrega: la pantalla lo dice en vez de
 * prometerla. El contador de borradores del plan se ve siempre (`URS §71`,
 * `§110`).
 *
 * Los números de cada ficha son RECUENTOS de la lista, no importes: esta web no
 * hace aritmética de dinero.
 */
import { useCallback, useEffect, useState } from "react";
import {
  api,
  apiImportacion,
  type CuentaCobros,
  type EstadoExportacion,
  type EstadoFactura,
  type ResumenCobros,
  type ResumenFactura,
  type UsoFacturas,
} from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible } from "../ui/formato";
import { Icono } from "../ui/Iconos";
import { InsigniaFactura } from "../ui/Insignia";
import { Aviso, Boton, FilaPulsable, Tarjeta } from "../ui/primitivas";
import { BotonExportacion, PanelExportacion } from "./PanelExportacion";

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" }
  | {
      readonly tipo: "datos";
      readonly facturas: readonly ResumenFactura[];
      readonly uso: UsoFacturas | null;
      /** Business (`OD-0040 D-04`): el estado de cobro, de la API. `null` fuera de Business o sin permiso. */
      readonly cobros: ResumenCobros | null;
      /** `OD-0036`: la exportación para la gestoría. `null` sin permiso o si la API no responde: sin botón. */
      readonly exportacion: EstadoExportacion | null;
    };

const ESTADOS: readonly EstadoFactura[] = ["DRAFT", "ISSUED", "VOIDED", "DISCARDED"];

/** La columna derivada de `OD-0040 D-04`: el estado de cobro de una emitida, si lo tiene. */
function CobroDeLaFila({ cuenta }: { readonly cuenta: CuentaCobros | null }) {
  if (cuenta === null || cuenta.paymentStatus === null || cuenta.blockedBy === "INVOICE_VOIDED") return null;
  return (
    <>
      {" · "}
      <span className={`insignia insignia-pago-${cuenta.paymentStatus}`}>{t.cobros.estados[cuenta.paymentStatus]}</span>
      {cuenta.paymentStatus === "PARTIALLY_PAID" ? ` · ${t.cobros.queda(importeLegible(cuenta.pending))}` : ""}
    </>
  );
}

export function PantallaFacturas({
  hayConexion,
  onAbrir,
  onNueva,
  onIrAPresupuestos,
  onVerPendientes,
  onVerImportadas,
  onSesionCaducada,
}: {
  hayConexion: boolean;
  onAbrir: (id: string) => void;
  onNueva: () => void;
  onIrAPresupuestos: () => void;
  /** OD-0051: la lista de cobros pendientes, desde la cifra de lo pendiente. */
  onVerPendientes: () => void;
  /** OD-0057 D-06: las facturas de otro programa, si se ha importado alguna. */
  onVerImportadas: () => void;
  onSesionCaducada: () => void;
}) {
  const [filtro, setFiltro] = useState<EstadoFactura | "TODAS">("TODAS");
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  const [exportacionAbierta, setExportacionAbierta] = useState(false);

  const cargar = useCallback(async (): Promise<void> => {
    setEstado({ tipo: "cargando" });
    const [lista, uso, cobros, exportacion] = await Promise.all([
      api.listarFacturas(null),
      api.usoFacturas(),
      api.resumenCobros(),
      api.estadoExportacion(),
    ]);
    if (!lista.ok) {
      if (lista.estado === 401) onSesionCaducada();
      else setEstado({ tipo: lista.estado === 403 ? "sin-permiso" : "error" });
      return;
    }
    setEstado({
      tipo: "datos",
      facturas: lista.datos,
      uso: uso.ok ? uso.datos : null,
      cobros: cobros.ok ? cobros.datos : null,
      exportacion: exportacion.ok ? exportacion.datos : null,
    });
  }, [onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // El enlace a las de otro programa solo aparece si hay alguna importada.
  const [hayImportadas, setHayImportadas] = useState(false);
  useEffect(() => {
    void apiImportacion.facturas({ q: "", cliente: null }).then((r) => setHayImportadas(r.ok && r.datos.facturas.length > 0));
  }, []);
  const enlaceImportadas = hayImportadas ? (
    <p className="ayuda-neutra">
      <button type="button" className="enlace" onClick={onVerImportadas}>
        {t.facturasImportadas.boton}
      </button>
    </p>
  ) : null;

  if (estado.tipo === "datos" && estado.facturas.length === 0) {
    return (
      <Tarjeta titulo={t.facturas.titulo}>
        <div className="estado-vacio">
          <span className="estado-vacio-icono" aria-hidden="true">
            <Icono nombre="facturas" />
          </span>
          <p className="estado-vacio-titulo">{t.facturas.vacioTitulo}</p>
          <p>{t.facturas.vacioDetalle}</p>
          <div className="acciones">
            <Boton onClick={onNueva}>
              <Icono nombre="mas" />
              {t.facturas.nueva}
            </Boton>
            <Boton variante="secundario" onClick={onIrAPresupuestos}>
              {t.facturas.desdePresupuesto}
            </Boton>
          </div>
        </div>
        {enlaceImportadas}
      </Tarjeta>
    );
  }

  const facturas = estado.tipo === "datos" ? estado.facturas : [];
  const uso = estado.tipo === "datos" ? estado.uso : null;
  const resumenCobros = estado.tipo === "datos" ? estado.cobros : null;
  const exportacion = estado.tipo === "datos" ? estado.exportacion : null;
  const cobroDe = new Map<string, CuentaCobros>((resumenCobros?.facturas ?? []).map((c) => [c.invoiceId, c]));
  const lleno = uso !== null && uso.draftsActive.available === 0;
  const cuenta = (e: EstadoFactura): number => facturas.filter((f) => f.status === e).length;
  const fichas: ReadonlyArray<{ readonly valor: EstadoFactura | "TODAS"; readonly cuenta: number }> = [
    { valor: "TODAS", cuenta: facturas.length },
    ...ESTADOS.filter((e) => cuenta(e) > 0 || e === filtro).map((e) => ({ valor: e, cuenta: cuenta(e) })),
  ];
  const visibles = filtro === "TODAS" ? facturas : facturas.filter((f) => f.status === filtro);

  return (
    <Tarjeta titulo={t.facturas.titulo}>
      {uso !== null ? (
        <div className="progreso">
          {uso.draftsActive.limit === null ? null : (
            <progress value={uso.draftsActive.used} max={uso.draftsActive.limit} aria-labelledby="cuota-borradores" />
          )}
          <p id="cuota-borradores" className="ayuda-neutra">
            {uso.draftsActive.limit === null
              ? t.facturas.cuotaBorradoresSinLimite(uso.draftsActive.used)
              : t.facturas.cuotaBorradores(uso.draftsActive.used, uso.draftsActive.limit)}
          </p>
        </div>
      ) : null}
      {lleno ? <Aviso tono="aviso">{t.facturas.cuotaLlena}</Aviso> : null}
      {/* D-08: las dos cifras las suma la API; aqui solo se escriben. */}
      {resumenCobros !== null ? (
        <p className="ayuda-neutra">
          {t.cobros.pendienteTotal(importeLegible(resumenCobros.pendiente), resumenCobros.facturasPendientes)} ·{" "}
          {t.cobros.cobradoMes(importeLegible(resumenCobros.cobrado))}
          {resumenCobros.facturasPendientes > 0 ? (
            <>
              {" · "}
              <button type="button" className="enlace" onClick={onVerPendientes}>
                {t.cobros.verPendientes}
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      {enlaceImportadas}

      <div className="barra-acciones">
        <div className="fichas" role="group" aria-label={t.facturas.filtro}>
          {fichas.map((f) => (
            <button key={f.valor} type="button" className="ficha" aria-pressed={filtro === f.valor} onClick={() => setFiltro(f.valor)}>
              {f.valor === "TODAS" ? null : <span className={`punto punto-factura-${f.valor}`} aria-hidden="true" />}
              <span>{f.valor === "TODAS" ? t.facturas.todas : t.facturas.estados[f.valor]}</span>
              <span className="ficha-cuenta">{f.cuenta}</span>
            </button>
          ))}
        </div>
        <div className="barra-acciones-botones">
          {/* OD-0036, arriba y no debajo de la lista: con muchas facturas, al
              final de la página no se veía (propietario, 2026-10-22). Sin el
              plan el botón sigue aquí y el panel lo explica. */}
          {exportacion !== null ? <BotonExportacion abierto={exportacionAbierta} onClick={() => setExportacionAbierta((v) => !v)} /> : null}
          <Boton onClick={onNueva} deshabilitado={lleno || estado.tipo !== "datos"}>
            {t.facturas.nueva}
          </Boton>
        </div>
      </div>

      {exportacion !== null && exportacionAbierta ? (
        <PanelExportacion estado={exportacion} hayConexion={hayConexion} onCerrar={() => setExportacionAbierta(false)} />
      ) : null}

      {estado.tipo === "cargando" ? <p>{t.facturas.cargando}</p> : null}
      {estado.tipo === "sin-permiso" ? <Aviso tono="aviso">{t.facturas.sinPermiso}</Aviso> : null}
      {estado.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.facturas.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.presupuestos.reintentar}
          </Boton>
        </>
      ) : null}

      {estado.tipo === "datos" && visibles.length === 0 ? <Aviso tono="info">{t.facturas.vacioFiltro}</Aviso> : null}
      {visibles.length > 0 ? (
        <ul className="lista-filas">
          {visibles.map((f) => (
            <FilaPulsable key={f.id} onClick={() => onAbrir(f.id)}>
              <span className="fila-titulo">
                {f.number ?? t.facturas.sinNumero} · {f.customerName ?? ""}
              </span>
              <span className="ayuda-neutra">
                {/* La insignia dice el estado DOCUMENTAL (URS §28): una
                    factura ya rectificada sigue ISSUED en la base, y decir
                    solo "Emitida" esconde lo unico que ha cambiado. */}
                <InsigniaFactura estado={f.documentState} respaldo={f.status} />
                {f.invoiceTotal === null ? ` · ${t.facturas.sinTotal}` : ` · ${importeLegible(f.invoiceTotal)} €`}
                {f.sourceQuoteId !== null ? ` · ${t.facturas.deUnPresupuesto}` : ""}
                <CobroDeLaFila cuenta={cobroDe.get(f.id) ?? null} />
              </span>
            </FilaPulsable>
          ))}
        </ul>
      ) : null}
    </Tarjeta>
  );
}
